import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ClientDashboardTabs from '../../components/navigation/ClientDashboardTabs';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import RoomCard from './components/RoomCard';
import RoomFormModal from './components/RoomFormModal';
import RoomPreviewModal from './components/RoomPreviewModal';
import BulkUploadModal from './components/BulkUploadModal';
import DeleteConfirmModal from './components/DeleteConfirmModal';

// Mock companyService for local use
const companyService = {
  getBySlug: async (slug) => {
    // Mock implementation - replace with actual API call
    return { slug, name: 'Mock Company' };
  }
};

const RoomsManagement = () => {
  const navigate = useNavigate();
  const { companyIdentifier } = useParams();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rooms, setRooms] = useState([]);
  const [filteredRooms, setFilteredRooms] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('recent');
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [viewMode, setViewMode] = useState('grid');

  const categories = ['Residential', 'Commercial', 'Industrial', 'Specialty'];

  useEffect(() => {
    // If accessed via company-specific route, load company data
    if (companyIdentifier) {
      const loadCompanyData = async () => {
        try {
          setLoading(true);
          // Extract slug from compound identifier
          const extractSlugFromIdentifier = (identifier) => {
            const parts = identifier?.split('-');
            let uuidStartIndex = -1;
            for (let i = parts?.length - 1; i >= 0; i--) {
              if (parts?.[i]?.length === 12 && /^[a-f0-9]+$/i?.test(parts?.[i])) {
                if (i >= 4 &&
                parts?.[i - 1]?.length === 4 && /^[a-f0-9]+$/i?.test(parts?.[i - 1]) &&
                parts?.[i - 2]?.length === 4 && /^[a-f0-9]+$/i?.test(parts?.[i - 2]) &&
                parts?.[i - 3]?.length === 4 && /^[a-f0-9]+$/i?.test(parts?.[i - 3]) &&
                parts?.[i - 4]?.length === 8 && /^[a-f0-9]+$/i?.test(parts?.[i - 4])) {
                  uuidStartIndex = i - 4;
                  break;
                }
              }
            }
            if (uuidStartIndex > 0) {
              return parts?.slice(0, uuidStartIndex)?.join('-');
            }
            return identifier;
          };

          const slug = extractSlugFromIdentifier(companyIdentifier);
          const normalizedSlug = decodeURIComponent(slug || '')?.toLowerCase()?.trim();

          const companyData = await companyService?.getBySlug(normalizedSlug);
          setCompany(companyData);
        } catch (error) {
          console.error('Error loading company data:', error);
        } finally {
          setLoading(false);
        }
      };

      loadCompanyData();
    } else {
      setLoading(false);
    }
  }, [companyIdentifier]);

  useEffect(() => {
    const mockRooms = [
    {
      id: 1,
      name: 'Modern Living Room',
      category: 'Residential',
      dimensions: "20\' x 15'",
      backgroundImage: "https://img.rocket.new/generatedImages/rocket_gen_img_14ce58ec9-1765835266619.png",
      backgroundImageAlt: 'Spacious modern living room with large windows, gray sectional sofa, wooden coffee table, and polished concrete flooring with natural sunlight',
      isDefault: true,
      lightingAdjustment: 0,
      usageCount: 247,
      createdAt: '2025-11-15T10:30:00.000Z',
      updatedAt: '2025-12-10T14:20:00.000Z'
    },
    {
      id: 2,
      name: 'Contemporary Kitchen',
      category: 'Residential',
      dimensions: "15\' x 12'",
      backgroundImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1c9889b36-1765159449018.png",
      backgroundImageAlt: 'Contemporary open-plan kitchen with white cabinets, marble countertops, stainless steel appliances, and light gray epoxy flooring under pendant lighting',
      isDefault: false,
      lightingAdjustment: 10,
      usageCount: 189,
      createdAt: '2025-11-18T09:15:00.000Z',
      updatedAt: '2025-12-08T11:45:00.000Z'
    },
    {
      id: 3,
      name: 'Retail Showroom',
      category: 'Commercial',
      dimensions: "40\' x 30'",
      backgroundImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1a722f487-1765913898211.png",
      backgroundImageAlt: 'Bright retail showroom interior with white walls, modern display fixtures, track lighting, and glossy commercial-grade flooring with reflective finish',
      isDefault: false,
      lightingAdjustment: -5,
      usageCount: 156,
      createdAt: '2025-11-20T13:00:00.000Z',
      updatedAt: '2025-12-05T16:30:00.000Z'
    },
    {
      id: 4,
      name: 'Warehouse Floor',
      category: 'Industrial',
      dimensions: "100\' x 80'",
      backgroundImage: "https://img.rocket.new/generatedImages/rocket_gen_img_12a187ca8-1764799847557.png",
      backgroundImageAlt: 'Large industrial warehouse interior with high ceilings, metal shelving units, fluorescent lighting, and durable concrete flooring with forklift tire marks',
      isDefault: false,
      lightingAdjustment: -10,
      usageCount: 98,
      createdAt: '2025-11-22T08:45:00.000Z',
      updatedAt: '2025-12-01T10:15:00.000Z'
    },
    {
      id: 5,
      name: 'Master Bedroom',
      category: 'Residential',
      dimensions: "18\' x 14'",
      backgroundImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1a5332b0a-1764793769742.png",
      backgroundImageAlt: 'Elegant master bedroom with king-size bed, neutral color palette, bedside tables with lamps, and smooth polished flooring reflecting ambient lighting',
      isDefault: false,
      lightingAdjustment: 5,
      usageCount: 134,
      createdAt: '2025-11-25T11:20:00.000Z',
      updatedAt: '2025-12-07T09:00:00.000Z'
    },
    {
      id: 6,
      name: 'Restaurant Dining Area',
      category: 'Commercial',
      dimensions: "35\' x 25'",
      backgroundImage: "https://images.unsplash.com/photo-1658110178995-e7fbe728bf11",
      backgroundImageAlt: 'Upscale restaurant dining area with wooden tables, upholstered chairs, warm ambient lighting, and dark polished epoxy flooring with subtle sheen',
      isDefault: false,
      lightingAdjustment: 0,
      usageCount: 112,
      createdAt: '2025-11-28T14:30:00.000Z',
      updatedAt: '2025-12-09T15:45:00.000Z'
    },
    {
      id: 7,
      name: 'Home Garage',
      category: 'Specialty',
      dimensions: "24\' x 20'",
      backgroundImage: "https://img.rocket.new/generatedImages/rocket_gen_img_193ee2927-1764798924046.png",
      backgroundImageAlt: 'Clean residential garage interior with white walls, organized tool storage, overhead lighting, and smooth gray epoxy-coated concrete floor',
      isDefault: false,
      lightingAdjustment: -8,
      usageCount: 176,
      createdAt: '2025-12-01T10:00:00.000Z',
      updatedAt: '2025-12-11T12:30:00.000Z'
    },
    {
      id: 8,
      name: 'Office Conference Room',
      category: 'Commercial',
      dimensions: "25\' x 18'",
      backgroundImage: "https://img.rocket.new/generatedImages/rocket_gen_img_14c745895-1764847975715.png",
      backgroundImageAlt: 'Professional conference room with large wooden table, ergonomic chairs, wall-mounted screen, recessed lighting, and polished commercial flooring',
      isDefault: false,
      lightingAdjustment: 3,
      usageCount: 87,
      createdAt: '2025-12-03T09:30:00.000Z',
      updatedAt: '2025-12-12T08:15:00.000Z'
    }];


    setRooms(mockRooms);
    setFilteredRooms(mockRooms);
  }, []);

  useEffect(() => {
    let filtered = [...rooms];

    if (searchTerm) {
      filtered = filtered?.filter((room) =>
      room?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      room?.category?.toLowerCase()?.includes(searchTerm?.toLowerCase())
      );
    }

    if (selectedCategory !== 'all') {
      filtered = filtered?.filter((room) => room?.category === selectedCategory);
    }

    switch (sortBy) {
      case 'recent':
        filtered?.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
        break;
      case 'popular':
        filtered?.sort((a, b) => b?.usageCount - a?.usageCount);
        break;
      case 'name':
        filtered?.sort((a, b) => a?.name?.localeCompare(b?.name));
        break;
      default:
        break;
    }

    setFilteredRooms(filtered);
  }, [searchTerm, selectedCategory, sortBy, rooms]);

  const handleAddRoom = () => {
    setSelectedRoom(null);
    setIsFormModalOpen(true);
  };

  const handleEditRoom = (room) => {
    setSelectedRoom(room);
    setIsFormModalOpen(true);
  };

  const handleDuplicateRoom = (room) => {
    const duplicatedRoom = {
      ...room,
      id: Date.now(),
      name: `${room?.name} (Copy)`,
      isDefault: false,
      usageCount: 0,
      createdAt: new Date()?.toISOString(),
      updatedAt: new Date()?.toISOString()
    };
    setRooms((prev) => [duplicatedRoom, ...prev]);
  };

  const handleDeleteRoom = (room) => {
    setSelectedRoom(room);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    setIsDeleting(true);
    setTimeout(() => {
      setRooms((prev) => prev?.filter((r) => r?.id !== selectedRoom?.id));
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
      setSelectedRoom(null);
    }, 800);
  };

  const handlePreviewRoom = (room) => {
    setSelectedRoom(room);
    setIsPreviewModalOpen(true);
  };

  const handleSaveRoom = (roomData) => {
    if (roomData?.id && rooms?.find((r) => r?.id === roomData?.id)) {
      setRooms((prev) => prev?.map((r) => r?.id === roomData?.id ? roomData : r));
    } else {
      setRooms((prev) => [roomData, ...prev]);
    }
  };

  const handleBulkUpload = (newRooms) => {
    setRooms((prev) => [...newRooms, ...prev]);
  };

  const categoryOptions = [
  { value: 'all', label: 'All Categories' },
  ...categories?.map((cat) => ({ value: cat, label: cat }))];


  const sortOptions = [
  { value: 'recent', label: 'Recently Updated' },
  { value: 'popular', label: 'Most Popular' },
  { value: 'name', label: 'Name (A-Z)' }];


  return (
    <div className="min-h-screen bg-background">
      {companyIdentifier && <ClientDashboardTabs />}
      <div className={companyIdentifier ? "main-content-with-tabs" : ""}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold text-text-primary mb-2">Rooms Management</h1>
              <p className="text-text-secondary">
                Configure room backgrounds for customer visualizer experiences
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={() => setIsBulkUploadModalOpen(true)}
                iconName="Upload"
                iconPosition="left">

                Bulk Upload
              </Button>
              <Button
                variant="default"
                onClick={handleAddRoom}
                iconName="Plus"
                iconPosition="left">

                Add Room
              </Button>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-2">
                <Input
                  type="search"
                  placeholder="Search rooms..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)} />

              </div>
              <Select
                placeholder="Filter by category"
                options={categoryOptions}
                value={selectedCategory}
                onChange={setSelectedCategory} />

              <Select
                placeholder="Sort by"
                options={sortOptions}
                value={sortBy}
                onChange={setSortBy} />

            </div>

            <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
              <div className="text-sm text-text-secondary">
                Showing {filteredRooms?.length} of {rooms?.length} room{rooms?.length !== 1 ? 's' : ''}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`w-8 h-8 flex items-center justify-center rounded transition-colors duration-150 ${
                  viewMode === 'grid' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`
                  }
                  aria-label="Grid view">

                  <Icon name="Grid3x3" size={16} />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`w-8 h-8 flex items-center justify-center rounded transition-colors duration-150 ${
                  viewMode === 'list' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`
                  }
                  aria-label="List view">

                  <Icon name="List" size={16} />
                </button>
              </div>
            </div>
          </div>

          {filteredRooms?.length === 0 ?
          <div className="bg-card border border-border rounded-lg p-12 text-center">
              <Icon name="Home" size={48} className="mx-auto mb-4 text-text-secondary" />
              <h3 className="text-lg font-semibold text-text-primary mb-2">No rooms found</h3>
              <p className="text-text-secondary mb-6">
                {searchTerm || selectedCategory !== 'all' ? 'Try adjusting your filters' : 'Get started by adding your first room'}
              </p>
              {!searchTerm && selectedCategory === 'all' &&
            <Button
              variant="default"
              onClick={handleAddRoom}
              iconName="Plus"
              iconPosition="left">

                  Add Your First Room
                </Button>
            }
            </div> :

          <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4'}>
              {filteredRooms?.map((room) =>
            <RoomCard
              key={room?.id}
              room={room}
              onEdit={handleEditRoom}
              onDuplicate={handleDuplicateRoom}
              onDelete={handleDeleteRoom}
              onPreview={handlePreviewRoom} />

            )}
            </div>
          }
        </div>
      </div>
      <RoomFormModal
        isOpen={isFormModalOpen}
        onClose={() => {
          setIsFormModalOpen(false);
          setSelectedRoom(null);
        }}
        onSave={handleSaveRoom}
        room={selectedRoom}
        categories={categories} />

      <RoomPreviewModal
        isOpen={isPreviewModalOpen}
        onClose={() => {
          setIsPreviewModalOpen(false);
          setSelectedRoom(null);
        }}
        room={selectedRoom} />

      <BulkUploadModal
        isOpen={isBulkUploadModalOpen}
        onClose={() => setIsBulkUploadModalOpen(false)}
        onUpload={handleBulkUpload}
        categories={categories} />

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedRoom(null);
        }}
        onConfirm={handleConfirmDelete}
        room={selectedRoom}
        isDeleting={isDeleting} />

    </div>);

};

export default RoomsManagement;